/**
 * Create User and Consumer Actor
 *
 * Creates a new user account and consumer record in sequence.
 * This actor executes two sequential API calls:
 * 1. Create user in auth system
 * 2. Create consumer and EHR patient record
 */

import { datadogLogs } from "@datadog/browser-logs";
import { fromPromise } from "xstate";
import { z } from "zod";
import {
  type CreateUserConsumerSuccess,
  createUserAndConsumer,
} from "../../server-actions/user-actions";

/**
 * Input Schema (What this actor needs)
 */
const CreateUserAndConsumerInputSchema = z.object({
  firstname: z.string().min(1),
  lastname: z.string().min(1),
  phone: z.string().min(1),
  email: z.string().email().or(z.literal("")),
  orgId: z.number(),
  dob: z.string(), // ISO date string
  preferredLanguage: z.string().optional(),
  ehrLocationId: z.string(),
  stripeCustomerId: z.string().optional(),
  primaryConsumerId: z.string().optional(),
  // biome-ignore lint/suspicious/noExplicitAny: need to resolve intakeFormSections types
  intakeFormSections: z.array(z.any()),
  // Existing IDs from context (to skip creation if already exists)
  userId: z.string().optional().nullable(),
  consumerId: z.string().optional().nullable(),
  userIdBackground: z.string().optional().nullable(),
  consumerIdBackground: z.string().optional().nullable(),
  sex: z.string().transform((s) => {
    const lower = s.toLowerCase();
    if (lower === "male") return "Male" as const;
    if (lower === "female") return "Female" as const;
    return "Unknown" as const;
  }),
});

type CreateUserAndConsumerInput = z.infer<
  typeof CreateUserAndConsumerInputSchema
>;

/**
 * Success Schema (What this actor returns on success)
 */
const CreateUserAndConsumerSuccessSchema = z.object({
  userId: z.string(),
  consumerId: z.string(),
});

/**
 * Error Schema (What this actor returns on failure)
 */
const CreateUserAndConsumerErrorSchema = z.object({
  code: z.enum([
    "INVALID_INPUT",
    "USER_CREATE_FAILED",
    "CONSUMER_CREATE_FAILED",
    "NETWORK_ERROR",
    "UNKNOWN_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
});

type CreateUserAndConsumerError = z.infer<
  typeof CreateUserAndConsumerErrorSchema
>;

/**
 * Actor metadata
 */
export interface ActorMetadata {
  displayName: string;
  description: string;
  category: "communication" | "data" | "auth" | "unknown";
  retryable: boolean;
  timeout: number;
  errorCodes: readonly string[];
  inputSchema: z.ZodSchema;
  successSchema: z.ZodSchema;
  errorSchema: z.ZodSchema;
  onSuccess: (result: unknown) => Record<string, unknown>;
  onError: (error: unknown) => Record<string, unknown>;
  composedActors?: readonly string[];
}

/**
 * Actor with metadata wrapper
 */
export interface ActorWithMetadata {
  // biome-ignore lint/suspicious/noExplicitAny: XState actor types are too complex for static typing
  impl: any;
  metadata: ActorMetadata;
}

/**
 * Create User and Consumer Actor Implementation
 *
 * Executes two sequential API calls via the server action.
 */
const createUserAndConsumerActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: CreateUserAndConsumerInput;
  }): Promise<CreateUserConsumerSuccess> => {
    // If the email on context is a non-empty string that fails email-format
    // validation, drop it before Zod validates. A dirty email (e.g. from a
    // stale consumer record or a failed email lookup) shouldn't block account
    // creation — the schema already accepts "" for a missing email.
    const sanitizedInput =
      typeof input.email === "string" &&
      input.email !== "" &&
      !z.string().email().safeParse(input.email).success
        ? { ...input, email: "" }
        : input;

    // Validate input
    const validated =
      CreateUserAndConsumerInputSchema.safeParse(sanitizedInput);

    if (!validated.success) {
      const errorData: CreateUserAndConsumerError = {
        code: "INVALID_INPUT",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    // Check if user and consumer already exist
    if (validated.data.userId && validated.data.consumerId) {
      return {
        userId: validated.data.userId,
        consumerId: validated.data.consumerId,
      };
    }

    if (
      validated.data.userIdBackground &&
      validated.data.consumerIdBackground
    ) {
      return {
        userId: validated.data.userIdBackground,
        consumerId: validated.data.consumerIdBackground,
      };
    }

    // Call server action (handles both API calls sequentially)
    const result = await createUserAndConsumer(validated.data);

    if (!result.success) {
      const errorData: CreateUserAndConsumerError = {
        code: result.error.code as CreateUserAndConsumerError["code"],
        message: result.error.message,
        retryable: result.error.retryable,
      };

      datadogLogs.logger.error(
        "[createUserAndConsumerActor] Failed to create user/consumer:",
        errorData,
      );

      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    datadogLogs.logger.log(
      "[createUserAndConsumerActor] Successfully created user and consumer:",
      result.data,
    );

    const { userId, consumerId } = result.data;
    datadogLogs.logger.info("kiosk consumer identified", {
      context: {
        action: "kiosk_consumer_identified",
        metadata: {
          consumerId,
          orgId: validated.data.orgId,
          identifiedVia: "new_patient",
        },
      },
    });

    return result.data;
  },
);

/**
 * Create User and Consumer Actor with Metadata
 */
export const createUserAndConsumerActor: ActorWithMetadata = {
  impl: createUserAndConsumerActorImpl,
  metadata: {
    displayName: "createUserAndConsumer",
    description:
      "Create user account and consumer record (two sequential API calls)",
    category: "data",
    retryable: true,
    timeout: 20000, // 20 seconds (two API calls)
    errorCodes: [
      "INVALID_INPUT",
      "USER_CREATE_FAILED",
      "CONSUMER_CREATE_FAILED",
      "NETWORK_ERROR",
      "UNKNOWN_ERROR",
    ],
    inputSchema: CreateUserAndConsumerInputSchema,
    successSchema: CreateUserAndConsumerSuccessSchema,
    errorSchema: CreateUserAndConsumerErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as CreateUserConsumerSuccess;
      return {
        userId: success.userId,
        consumerId: success.consumerId,
        userCreated: true,
      };
    },
    onError: (error: unknown) => {
      const err = error as CreateUserAndConsumerError;
      return {
        userCreated: false,
        userCreationError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  },
};
